import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiUrl } from "@/lib/api-client";
import { toAbsoluteAssetUrl } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { selectionItemOutlineClasses } from "@/lib/selection-item-styles";
import { ArrowLeft, Loader2, Camera, CheckSquare, Square, Upload, Filter, Search, Trash2, MoreHorizontal, X } from "lucide-react";
import type { ImageFolderPhoto, SubfolderType } from "@shared/api";
import PhotoViewer from "@/components/images/PhotoViewer";
import { GalleryMapView, filterGeoPhotos } from "@/components/images/GalleryMapView";
import { MultiPhotoCapture } from "@/components/images/MultiPhotoCapture";
import { SocialAssetsPreview, SocialComposer } from "@/components/images/SocialComposer";
import { FabMenu } from "@/components/ui/fab-menu";
import { AppSheet } from "@/components/ui/app-sheet";
import { PageTitleBar } from "@/components/ui/PageTitleBar";
import { SelectionToolbarLayout } from "@/components/ui/selection-toolbar-layout";
import { useIsMobile } from "@/hooks/use-mobile";
import { queryKeys } from "@/lib/query-keys";
import { useGalleryPhotoFacets } from "@/hooks/use-gallery-photo-facets";
import { type GpsUploadSource } from "@/lib/gallery-upload-gps";
import { uploadImages } from "@/lib/image-upload";
import { notify } from "@/lib/notify";
import { hapticTap } from "@/lib/haptics";
import { useLongPress } from "@/hooks/use-long-press";
import { useSelectionKeyboardShortcuts } from "@/hooks/use-selection-keyboard-shortcuts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ImagesSectionTabs } from "@/components/images/ImagesSectionTabs";
import type { ImageFolderFilters } from "@shared/api";
import ImageFilters from "@/components/images/ImageFilters";
import { readImageFiltersFromQuery, writeImageFiltersToQuery } from "@/lib/image-filter-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { filterImageFiles, dataTransferHasFilePayload } from "@/lib/filter-image-drop-files";
import { uploadImageFilesToCentralGallery } from "@/lib/upload-image-files-to-central-gallery";

const PAGE_SIZE = 50;

/** Ugyanaz a logika, mint a ImagesFolder: ritkán eltérő fájlnév / kiterjesztés miatt ne maradjon thumb nélkül feltöltés után. */
function findLocalFileForGalleryPhoto(photo: { file_name?: string }, files: File[]): File | undefined {
  const name = photo.file_name ?? "";
  if (!name) return undefined;
  const exact = files.find((f) => f.name === name);
  if (exact) return exact;
  const strip = (n: string) => n.replace(/\.[^/.]+$/, "");
  const base = strip(name);
  return files.find((f) => {
    const fb = strip(f.name);
    return fb === base || name.includes(fb) || f.name.includes(base);
  });
}

async function queueThumbnailsAfterUpload(
  files: File[],
  folderId: string,
  photos: Array<{ id: string; file_name?: string; subfolder_type?: string }>
) {
  const { generateAndUploadThumbnail } = await import("@/lib/image-thumbnail");
  for (const photo of photos) {
    const file = findLocalFileForGalleryPhoto(photo, files);
    if (!file || !photo.id) continue;
    const st = photo.subfolder_type;
    const sub: SubfolderType =
      st === "bemeres" || st === "munkaink" || st === "marketing" ? st : "munkaink";
    void generateAndUploadThumbnail(file, folderId, sub, photo.id).catch(() => {
      /* nem kritikus */
    });
  }
}

/** `?galleryMapDebug=1` vagy sessionStorage `galleryMapDebug=1` — részletes console + CF log útmutató: AGENTS.md */
function galleryMapDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("galleryMapDebug") === "1") return true;
    return sessionStorage.getItem("galleryMapDebug") === "1";
  } catch {
    return false;
  }
}

/** Photo as returned by GET /api/images/photos/raw (includes folder_name) */
export type RawGalleryPhoto = ImageFolderPhoto & { folder_name?: string };

function photoSortInstant(p: RawGalleryPhoto): Date {
  return new Date(p.gallery_sort_at || p.created_at);
}

function formatUploadedAt(
  createdAt: string | undefined,
  locale: string,
  gallerySortAt?: string | null
): string {
  const iso = gallerySortAt || createdAt;
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Format date only (no time) for visible range display */
function formatDateOnly(
  createdAt: string | undefined,
  locale: string,
  gallerySortAt?: string | null
): string {
  const iso = gallerySortAt || createdAt;
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(locale, {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
}

/** Group photos by month only (no day breaks – continuous flow within month) */
function groupByMonthOnly(photos: RawGalleryPhoto[], locale: string) {
  const byMonth: Record<string, RawGalleryPhoto[]> = {};
  for (const p of photos) {
    const d = photoSortInstant(p);
    const y = d.getFullYear();
    const m = d.getMonth();
    const monthKey = `${y}-${String(m + 1).padStart(2, "0")}`;
    if (!byMonth[monthKey]) byMonth[monthKey] = [];
    byMonth[monthKey].push(p);
  }
  const monthLabels: Record<string, string> = {};
  for (const monthKey of Object.keys(byMonth)) {
    const [y, m] = monthKey.split("-").map(Number);
    const date = new Date(y, m - 1, 1);
    monthLabels[monthKey] = date.toLocaleDateString(locale, { year: "numeric", month: "long" });
    byMonth[monthKey].sort(
      (a, b) => photoSortInstant(b).getTime() - photoSortInstant(a).getTime()
    );
  }
  return { byMonth, monthLabels };
}

function GalleryPhotoTile(props: {
  photo: RawGalleryPhoto;
  monthKey: string;
  index: number;
  isMobile: boolean;
  selectionMode: boolean;
  isSelected: boolean;
  onEnterSelectionMode: () => void;
  onToggle: () => void;
  onOpenViewer: () => void;
}) {
  const {
    photo,
    monthKey,
    index,
    isMobile,
    selectionMode,
    isSelected,
    onEnterSelectionMode,
    onToggle,
    onOpenViewer,
  } = props;

  const longPress = useLongPress({
    enabled: isMobile && !selectionMode,
    onLongPress: onEnterSelectionMode,
  });

  return (
    <button
      type="button"
      data-gallery-month={monthKey}
      data-gallery-index={index}
      className={cn(
        "gallery-photo text-left rounded overflow-hidden border border-border/20 relative transition-all",
        // Kijelölés módban a bal csík a fő jelzés — hover ring elkerülése; normál módban marad a ring.
        selectionMode
          ? "hover:shadow-md hover:border-border/35"
          : "hover:ring-2 hover:ring-primary/30 hover:shadow-md",
        // Ugyanakkora bal keret kijelölés nélkül is → nincs „ugrás” Vybrať bekapcsolásakor.
        selectionMode
          ? selectionItemOutlineClasses(true, isSelected)
          : "border-l-4 border-l-transparent",
      )}
      {...longPress}
      onClick={() => (selectionMode ? onToggle() : onOpenViewer())}
    >
      <div className="aspect-square overflow-hidden bg-muted relative">
        <img
          src={toAbsoluteAssetUrl(photo.thumbnail_url || photo.file_url)}
          alt={photo.file_name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {selectionMode && (
          <div className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded bg-background/80 border">
            {isSelected ? (
              <CheckSquare className="h-4 w-4 text-primary fill-current" />
            ) : (
              <Square className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        )}
      </div>
    </button>
  );
}

export default function ImagesRawGallery() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFilterState = readImageFiltersFromQuery(searchParams);
  const [filters, setFilters] = useState<ImageFolderFilters>(initialFilterState.filters);
  const [searchName, setSearchName] = useState(initialFilterState.searchName);
  const [dateFilterEnabled, setDateFilterEnabled] = useState(initialFilterState.dateFilterEnabled);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  const locale = i18n.language || "hu";
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const mapsApiKey = typeof import.meta.env.VITE_GOOGLE_MAPS_API_KEY === "string"
    ? import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    : "";

  /** Galéria rács: `/images/gallery`, térkép: `/images/maps` */
  const viewMode: "grid" | "map" = location.pathname === "/images/maps" ? "map" : "grid";
  const isGalleryRoute = location.pathname === "/images/gallery";

  useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get("galleryMapDebug") === "1") {
        sessionStorage.setItem("galleryMapDebug", "1");
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (viewMode !== "map") return;
    let debug = false;
    try {
      debug =
        sessionStorage.getItem("galleryMapDebug") === "1" ||
        new URLSearchParams(window.location.search).get("galleryMapDebug") === "1";
    } catch {
      /* ignore */
    }
    if (!debug) return;

    const redactedKey =
      typeof mapsApiKey === "string" && mapsApiKey.trim().length >= 8
        ? `${mapsApiKey.trim().slice(0, 4)}…${mapsApiKey.trim().slice(-4)}`
        : mapsApiKey.trim().length
          ? "(too-short)"
          : "(missing)";

    const scripts = Array.from(document.querySelectorAll("script")).filter((s) => {
      const src = s.getAttribute("src") || "";
      return src.includes("maps.googleapis.com/maps/api/js");
    });
    const scriptSrcs = scripts.map((s) => s.getAttribute("src") || "");
    const scriptHasKeyParam = scriptSrcs.some((src) => /[?&]key=/.test(src));
    const scriptKeyLooksEmpty = scriptSrcs.some((src) => /[?&]key=(&|$)/.test(src));

    console.warn("[GALLERY-MAP-DEBUG] env + script", {
      host: window.location.host,
      path: window.location.pathname,
      viewMode,
      viteMode: import.meta.env.MODE,
      viteProd: import.meta.env.PROD,
      apiKeyPresent: mapsApiKey.trim().length > 0,
      apiKeyLength: mapsApiKey.trim().length,
      apiKeyRedacted: redactedKey,
      scriptCount: scripts.length,
      scriptHasKeyParam,
      scriptKeyLooksEmpty,
      scriptSrcs,
    });
  }, [mapsApiKey, viewMode]);

  const [photos, setPhotos] = useState<RawGalleryPhoto[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [viewerState, setViewerState] = useState<{
    photos: RawGalleryPhoto[];
    index: number;
  } | null>(null);

  const [uploadSheetOpen, setUploadSheetOpen] = useState(false);
  const [addToDeal, setAddToDeal] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [uploadSubfolderType, setUploadSubfolderType] = useState<"bemeres" | "munkaink" | "marketing">("munkaink");
  const [uploadBusy, setUploadBusy] = useState(false);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addToDealSheetOpen, setAddToDealSheetOpen] = useState(false);
  const [socialSheetOpen, setSocialSheetOpen] = useState(false);
  const [generatedSocialAssets, setGeneratedSocialAssets] = useState<import("@shared/api").SocialAsset[]>([]);
  const [dealSearchValue, setDealSearchValue] = useState("");
  const [moveSubfolderType, setMoveSubfolderType] = useState<"bemeres" | "munkaink" | "marketing">("munkaink");
  const [moveBusy, setMoveBusy] = useState(false);
  const [galleryBulkDeleteOpen, setGalleryBulkDeleteOpen] = useState(false);
  const [galleryDeleteBusy, setGalleryDeleteBusy] = useState(false);
  const bulkHeaderDismissRef = useRef<HTMLButtonElement>(null);

  const [galleryDropDialogOpen, setGalleryDropDialogOpen] = useState(false);
  const [galleryDropPendingFiles, setGalleryDropPendingFiles] = useState<File[]>([]);
  const [galleryUploadSeedFiles, setGalleryUploadSeedFiles] = useState<File[] | null>(null);
  const [galleryDropQuickBusy, setGalleryDropQuickBusy] = useState(false);
  const [galleryFileDragHighlight, setGalleryFileDragHighlight] = useState(false);

  const clearGalleryUploadSeed = useCallback(() => setGalleryUploadSeedFiles(null), []);

  const fileDropZoneActive = isGalleryRoute && viewMode === "grid";

  useEffect(() => {
    const next = writeImageFiltersToQuery(searchParams, {
      filters,
      searchName,
      dateFilterEnabled,
    });
    next.delete("view");
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [filters, searchName, dateFilterEnabled, searchParams, setSearchParams]);

  /** Régi könyvjelzők: `?view=map` → `/images/maps` */
  useEffect(() => {
    if (location.pathname !== "/images/gallery") return;
    if (searchParams.get("view") !== "map") return;
    const next = new URLSearchParams(searchParams);
    next.delete("view");
    const q = next.toString();
    navigate(`/images/maps${q ? `?${q}` : ""}`, { replace: true });
  }, [location.pathname, searchParams, navigate]);

  /** Visible viewport range per month: date of top-left and bottom-right visible photo */
  const [visibleRangeByMonth, setVisibleRangeByMonth] = useState<Record<string, { first: string; last: string }>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  /** Galéria rács: thumb próba kész (siker vagy kudarc), munkamenetenként egyszer. */
  const thumbBackfillAttemptedRef = useRef<Set<string>>(new Set());
  /** Épp generálunk: következő effect futás ne indítson duplán ugyanarra az id-re. */
  const thumbInFlightRef = useRef<Set<string>>(new Set());

  const { byMonth, monthLabels } = useMemo(
    () => groupByMonthOnly(photos, locale),
    [photos, locale]
  );

  const updateVisibleRanges = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !byMonth) return;
    const rect = container.getBoundingClientRect();
    const els = container.querySelectorAll<HTMLElement>("[data-gallery-month][data-gallery-index]");
    const visibleByMonth: Record<string, Set<number>> = {};
    els.forEach((el) => {
      const r = el.getBoundingClientRect();
      const inView = r.bottom >= rect.top && r.top <= rect.bottom && r.right >= rect.left && r.left <= rect.right;
      if (!inView) return;
      const monthKey = el.getAttribute("data-gallery-month");
      const index = el.getAttribute("data-gallery-index");
      if (monthKey == null || index == null) return;
      const i = parseInt(index, 10);
      if (!visibleByMonth[monthKey]) visibleByMonth[monthKey] = new Set();
      visibleByMonth[monthKey].add(i);
    });
    const next: Record<string, { first: string; last: string }> = {};
    Object.keys(visibleByMonth).forEach((monthKey) => {
      const indices = visibleByMonth[monthKey];
      if (!indices.size) return;
      const monthPhotos = byMonth[monthKey];
      if (!monthPhotos?.length) return;
      const minI = Math.min(...indices);
      const maxI = Math.max(...indices);
      const firstStr = formatDateOnly(
        monthPhotos[minI].created_at,
        i18n.language || "hu",
        monthPhotos[minI].gallery_sort_at
      );
      const lastStr = formatDateOnly(
        monthPhotos[maxI].created_at,
        i18n.language || "hu",
        monthPhotos[maxI].gallery_sort_at
      );
      next[monthKey] = firstStr === lastStr ? { first: firstStr, last: firstStr } : { first: firstStr, last: lastStr };
    });
    setVisibleRangeByMonth((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
  }, [byMonth, i18n.language]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    updateVisibleRanges();
    const onScroll = () => requestAnimationFrame(updateVisibleRanges);
    container.addEventListener("scroll", onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateVisibleRanges());
    ro.observe(container);
    return () => {
      container.removeEventListener("scroll", onScroll);
      ro.disconnect();
    };
  }, [updateVisibleRanges, loading, photos.length, viewMode]);

  const geoQuery = useQuery({
    queryKey: [...queryKeys.galleryGeo, filters, searchName, dateFilterEnabled],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set("limit", "2000");
      if (searchName.trim()) params.set("name", searchName.trim());
      if (dateFilterEnabled && filters.dateFrom) params.set("dateFrom", filters.dateFrom);
      if (dateFilterEnabled && filters.dateTo) params.set("dateTo", filters.dateTo);
      (["panelStructure", "brand", "folderType", "model", "color", "prelisType", "category"] as const).forEach((key) => {
        const raw = (filters as Record<string, unknown>)[key];
        if (!raw) return;
        (Array.isArray(raw) ? raw : [raw]).forEach((v) => params.append(key, String(v)));
      });
      const r = await fetch(apiUrl(`/api/images/photos/geo?${params.toString()}`), {
        credentials: "include",
        signal,
      });
      if (!r.ok) throw new Error("geo_failed");
      const data = (await r.json()) as { photos?: RawGalleryPhoto[] };
      const raw = data.photos ?? [];
      if (galleryMapDebugEnabled()) {
        const s = raw[0];
        const after = filterGeoPhotos(raw);
        console.warn("[GALLERY-MAP-DEBUG] GET /api/images/photos/geo", {
          status: r.status,
          rawRowCount: raw.length,
          afterClientCoordinateFilter: after.length,
          sampleFirstRow: s
            ? {
                id: s.id,
                exif_latitude: s.exif_latitude,
                exif_longitude: s.exif_longitude,
                latType: typeof (s as { exif_latitude?: unknown }).exif_latitude,
                lngType: typeof (s as { exif_longitude?: unknown }).exif_longitude,
              }
            : null,
        });
      }
      return filterGeoPhotos(raw);
    },
    enabled: viewMode === "map",
  });

  const handleMapPhotoOpen = useCallback(
    (_photo: RawGalleryPhoto, index: number) => {
      const list = geoQuery.data;
      if (!list?.length) return;
      setViewerState({ photos: list, index });
    },
    [geoQuery.data]
  );

  const handleMapClusterOpen = useCallback((clusterPhotos: RawGalleryPhoto[]) => {
    if (clusterPhotos.length < 2) return;
    setViewerState({ photos: clusterPhotos, index: 0 });
  }, []);

  const boardQuery = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async ({ signal }) => {
      const r = await fetch(apiUrl("/api/crm/board"), { credentials: "include", signal });
      if (!r.ok) throw new Error("Failed to load deals");
      return r.json() as Promise<{ deals?: Array<{ id: string; title?: string; contactId?: string }> }>;
    },
    enabled: addToDealSheetOpen || uploadSheetOpen,
  });
  const deals = boardQuery.data?.deals || [];
  const dealSearchLower = dealSearchValue.trim().toLowerCase();
  const filteredDeals = useMemo(
    () =>
      dealSearchLower
        ? deals.filter(
            (d) =>
              (d.title || "").toLowerCase().includes(dealSearchLower) ||
              (d.id || "").toLowerCase().includes(dealSearchLower)
          )
        : deals,
    [deals, dealSearchLower]
  );

  const loadPage = useCallback(async (before?: string | null) => {
    const params = new URLSearchParams();
    params.set("limit", String(PAGE_SIZE));
    if (before) params.set("before", before);
    if (searchName.trim()) params.set("name", searchName.trim());
    if (dateFilterEnabled && filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (dateFilterEnabled && filters.dateTo) params.set("dateTo", filters.dateTo);
    (["panelStructure", "brand", "folderType", "model", "color", "prelisType", "category"] as const).forEach((key) => {
      const raw = (filters as Record<string, unknown>)[key];
      if (!raw) return;
      (Array.isArray(raw) ? raw : [raw]).forEach((v) => params.append(key, String(v)));
    });
    const r = await fetch(apiUrl(`/api/images/photos/raw?${params.toString()}`), {
      credentials: "include",
    });
    if (!r.ok) throw new Error(t("images.errorLoadFolders"));
    const data = await r.json();
    return { photos: (data.photos || []) as RawGalleryPhoto[], nextBefore: data.nextBefore ?? null };
  }, [t, searchName, dateFilterEnabled, filters]);

  useEffect(() => {
    if (!isGalleryRoute) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    loadPage(null)
      .then(({ photos: p, nextBefore: nb }) => {
        if (!cancelled) {
          setPhotos(p);
          setNextBefore(nb);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadPage, isGalleryRoute]);

  const dateRangeQuery = useQuery({
    queryKey: ["image-folders-date-range"],
    queryFn: async () => {
      const r = await fetch(apiUrl("/api/images/folders/date-range"), { credentials: "include" });
      if (!r.ok) return { minDate: undefined, maxDate: undefined };
      const data = await r.json();
      return { minDate: data.minDate as string | undefined, maxDate: data.maxDate as string | undefined };
    },
  });

  const loadMore = useCallback(() => {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    loadPage(nextBefore)
      .then(({ photos: p, nextBefore: nb }) => {
        setPhotos((prev) => [...prev, ...p]);
        setNextBefore(nb);
      })
      .finally(() => setLoadingMore(false));
  }, [nextBefore, loadingMore, loadPage]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!nextBefore || loadingMore) return;
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { rootMargin: "200px", threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [nextBefore, loadingMore, loadMore]);

  useEffect(() => {
    if (!isGalleryRoute || viewMode !== "grid" || loading || error) return;
    const missing = photos.filter(
      (p) =>
        !p.thumbnail_url &&
        p.file_url &&
        !thumbBackfillAttemptedRef.current.has(p.id) &&
        !thumbInFlightRef.current.has(p.id)
    );
    if (missing.length === 0) return;

    const CONCURRENCY = 3;
    void (async () => {
      const { generateAndUploadThumbnailFromExistingPhoto } = await import("@/lib/image-thumbnail");
      for (let i = 0; i < missing.length; i += CONCURRENCY) {
        const batch = missing.slice(i, i + CONCURRENCY);
        for (const p of batch) {
          thumbInFlightRef.current.add(p.id);
        }
        await Promise.all(
          batch.map(async (p) => {
            try {
              const url = await generateAndUploadThumbnailFromExistingPhoto(p);
              if (url) {
                setPhotos((prev) => prev.map((x) => (x.id === p.id ? { ...x, thumbnail_url: url } : x)));
              }
            } finally {
              thumbInFlightRef.current.delete(p.id);
              thumbBackfillAttemptedRef.current.add(p.id);
            }
          })
        );
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
      void queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
    })();
  }, [isGalleryRoute, viewMode, loading, error, photos, queryClient]);

  const flatPhotos = photos;
  const galleryFacetsQuery = useGalleryPhotoFacets();
  const galleryOptionCounts = galleryFacetsQuery.data;
  const getPhotoIndex = (photoId: string) => flatPhotos.findIndex((p) => p.id === photoId);

  const handleGalleryUpload = useCallback(
    async (files: File[], sources: GpsUploadSource[]) => {
      if (files.length === 0) return;
      setUploadBusy(true);
      try {
        const onEachSuccess = (response: unknown) => {
          const data = response as {
            photos?: Array<{ id: string; file_name?: string; subfolder_type?: string }>;
            folderId?: string;
          } | null;
          if (data?.folderId && data.photos?.length) {
            void queueThumbnailsAfterUpload(files, data.folderId, data.photos);
          }
        };

        const res =
          addToDeal && selectedDealId
            ? await uploadImages({
                uploadUrl: apiUrl("/api/images/photos/upload-to-deal"),
                files,
                sources,
                extraFields: { dealId: selectedDealId, subfolderType: uploadSubfolderType },
                context: { folderId: "", dealId: selectedDealId },
                onEachSuccess,
              })
            : await uploadImages({
                uploadUrl: apiUrl("/api/images/photos/upload-to-gallery"),
                files,
                sources,
                context: { folderId: "" },
                onEachSuccess,
              });

        if (res.queued > 0) {
          notify.info(
            t("uploadOutbox.queuedBatch", { count: res.queued, defaultValue: "{{count}} feltöltés sorba állítva" }),
            t("uploadOutbox.queuedBatchDesc", { defaultValue: "A kapcsolat helyreállásakor automatikusan feltöltjük." }),
          );
        }
        void queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
        void queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
        loadPage(null).then(({ photos: p, nextBefore: nb }) => {
          setPhotos(p);
          setNextBefore(nb);
        });
        setUploadSheetOpen(false);
        window.setTimeout(() => {
          void loadPage(null).then(({ photos: p, nextBefore: nb }) => {
            setPhotos(p);
            setNextBefore(nb);
          });
          void queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
          void queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
        }, 2500);
      } catch (e) {
        alert(e instanceof Error ? e.message : "Feltöltés hiba.");
      } finally {
        setUploadBusy(false);
      }
    },
    [addToDeal, selectedDealId, uploadSubfolderType, loadPage, queryClient, t]
  );

  const closeGalleryDropDialog = useCallback(() => {
    setGalleryDropDialogOpen(false);
    setGalleryDropPendingFiles([]);
  }, []);

  const confirmGalleryDropQuickUpload = useCallback(async () => {
    const files = galleryDropPendingFiles;
    if (files.length === 0) return;
    setGalleryDropQuickBusy(true);
    try {
      await uploadImageFilesToCentralGallery(files, queryClient);
      const { photos: p, nextBefore: nb } = await loadPage(null);
      setPhotos(p);
      setNextBefore(nb);
      window.setTimeout(() => {
        void loadPage(null).then(({ photos: p2, nextBefore: nb2 }) => {
          setPhotos(p2);
          setNextBefore(nb2);
        });
        void queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
        void queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      }, 2500);
      toast({
        title: t("images.galleryDropQuickSuccess"),
        description: t("images.galleryDropQuickSuccessDesc", { count: files.length }),
      });
      closeGalleryDropDialog();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Feltöltés hiba.");
    } finally {
      setGalleryDropQuickBusy(false);
    }
  }, [galleryDropPendingFiles, queryClient, loadPage, t, closeGalleryDropDialog]);

  const openGalleryDropUploadSheet = useCallback(() => {
    const files = galleryDropPendingFiles;
    if (files.length === 0) return;
    setGalleryUploadSeedFiles([...files]);
    setGalleryDropDialogOpen(false);
    setGalleryDropPendingFiles([]);
    setUploadSheetOpen(true);
  }, [galleryDropPendingFiles]);

  const togglePhotoSelection = useCallback((photoId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) next.delete(photoId);
      else next.add(photoId);
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedIds(new Set());
  }, []);

  /** Térképen nincs tömeges kijelölés — tab váltáskor ne maradjon „Vybrať” állapot a fejlécben / state-ben. */
  useEffect(() => {
    if (viewMode === "map") exitSelectionMode();
  }, [viewMode, exitSelectionMode]);

  const selectAllGalleryPhotosInList = useCallback(() => {
    setSelectedIds(new Set(photos.map((p) => p.id)));
  }, [photos]);

  useSelectionKeyboardShortcuts({
    enabled: selectionMode && viewMode === "grid",
    onExit: exitSelectionMode,
    onSelectAllInView: selectAllGalleryPhotosInList,
  });

  const handleAddToDeal = useCallback(
    async (dealId: string) => {
      if (selectedIds.size === 0) return;
      setMoveBusy(true);
      try {
        const r = await fetch(apiUrl("/api/images/photos/move-to-folder"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            photoIds: Array.from(selectedIds),
            dealId,
            targetSubfolderType: moveSubfolderType,
          }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.userMessage || err?.message || "Áthelyezés sikertelen.");
        }
        setAddToDealSheetOpen(false);
        setSelectionMode(false);
        setSelectedIds(new Set());
        void queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
        void queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
        loadPage(null).then(({ photos: p, nextBefore: nb }) => {
          setPhotos(p);
          setNextBefore(nb);
        });
      } catch (e) {
        alert(e instanceof Error ? e.message : "Áthelyezés hiba.");
      } finally {
        setMoveBusy(false);
      }
    },
    [selectedIds, moveSubfolderType, loadPage, queryClient]
  );

  const confirmGalleryBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    const byFolder = new Map<string, string[]>();
    for (const p of photos) {
      if (!selectedIds.has(p.id)) continue;
      const fid = p.folder_id;
      if (!fid) continue;
      const list = byFolder.get(fid) ?? [];
      list.push(p.id);
      byFolder.set(fid, list);
    }
    if (byFolder.size === 0) {
      toast({
        variant: "destructive",
        title: t("images.galleryBulkDeleteError"),
        description: t("images.galleryBulkDeleteNoFolderId"),
      });
      setGalleryBulkDeleteOpen(false);
      return;
    }
    setGalleryDeleteBusy(true);
    try {
      for (const [folderId, photoIds] of byFolder) {
        const r = await fetch(apiUrl(`/api/images/folders/${folderId}/photos/batch`), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ photoIds }),
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(
            typeof err?.message === "string" ? err.message : `HTTP ${r.status}`
          );
        }
      }
      const removed = new Set(selectedIds);
      setPhotos((prev) => prev.filter((p) => !removed.has(p.id)));
      setSelectedIds(new Set());
      setSelectionMode(false);
      setGalleryBulkDeleteOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.galleryGeo });
      void queryClient.invalidateQueries({ queryKey: queryKeys.galleryPhotoFacets });
      toast({ title: t("images.galleryBulkDeleteSuccess") });
    } catch (e) {
      toast({
        variant: "destructive",
        title: t("images.galleryBulkDeleteError"),
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setGalleryDeleteBusy(false);
    }
  }, [photos, selectedIds, queryClient, t]);

  if (error) {
    return (
      <div className="p-6">
        <Button variant="ghost" size="sm" onClick={() => navigate("/images/projects")} className="mb-4 gap-2">
          <ArrowLeft className="h-4 w-4" />
          {t("images.backToFolders")}
        </Button>
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  const gallerySelectionActions = (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 w-9 shrink-0 p-0"
        aria-label={t("images.cancelSelection")}
        onClick={() => {
          hapticTap(12);
          exitSelectionMode();
        }}
      >
        <X className="h-4 w-4" />
      </Button>
      <span className="max-w-[10rem] truncate text-sm text-muted-foreground sm:max-w-none">
        {selectedIds.size} {t("images.selected")}
      </span>
      <Button
        variant="default"
        size="sm"
        className="shrink-0"
        onClick={() => setAddToDealSheetOpen(true)}
        disabled={selectedIds.size === 0}
      >
        {t("images.addToDeal")}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9 w-9 shrink-0 p-0" aria-label={t("common.more")}>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              setGeneratedSocialAssets([]);
              setSocialSheetOpen(true);
            }}
            disabled={selectedIds.size === 0}
          >
            {t("social.openComposer")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setGalleryBulkDeleteOpen(true)}
            disabled={selectedIds.size === 0 || galleryDeleteBusy}
            className="text-destructive focus:text-destructive"
          >
            {t("images.deleteSelectedPhotos")}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              hapticTap(12);
              exitSelectionMode();
            }}
          >
            {t("images.cancelSelection")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Fixed header – same as Manuals/Images/RouteGenerator */}
      {/* Felső sor: mint RouteGenerator – csak cím + akciók */}
      <div className="relative z-20 flex h-[60px] flex-shrink-0 overflow-hidden border-b bg-background">
        {viewMode === "grid" && !isMobile ? (
          <>
            <div
              className={cn(
                "absolute inset-0 flex items-center transition-all duration-300 ease-in-out",
                selectionMode ? "pointer-events-none -translate-y-full opacity-0" : "translate-y-0 opacity-100",
              )}
            >
              <PageTitleBar
                className="h-full min-w-0 flex-1 border-0"
                rowClassName="h-full min-h-0 items-center gap-2"
                title={t("images.gallery")}
                right={
                  <div className="flex shrink-0 items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      disabled={photos.length === 0}
                      onClick={() => {
                        hapticTap(12);
                        setSelectionMode(true);
                      }}
                    >
                      {t("common.selectTrigger")}
                    </Button>
                    <Button size="sm" type="button" onClick={() => setUploadSheetOpen(true)}>
                      <Upload className="mr-2 h-4 w-4" />
                      {t("images.uploadPhotosTitle")}
                    </Button>
                  </div>
                }
              />
            </div>
            <div
              className={cn(
                "absolute inset-0 flex items-center px-3 transition-all duration-300 ease-in-out sm:px-4",
                selectionMode ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-full opacity-0",
              )}
            >
              <SelectionToolbarLayout
                closeButtonRef={bulkHeaderDismissRef}
                className="px-0 sm:px-0"
                selectedCount={selectedIds.size}
                onClose={() => {
                  hapticTap(12);
                  exitSelectionMode();
                }}
                onSelectAll={() => setSelectedIds(new Set(photos.map((p) => p.id)))}
                selectAllDisabled={photos.length === 0}
                bulkActions={
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" type="button" className="shrink-0 gap-1">
                        {t("crm.more")}
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem
                        onClick={() => {
                          setGeneratedSocialAssets([]);
                          setSocialSheetOpen(true);
                        }}
                        disabled={selectedIds.size === 0}
                      >
                        {t("social.openComposer")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                }
                rightActions={
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setAddToDealSheetOpen(true)}
                    disabled={selectedIds.size === 0}
                  >
                    {t("images.addToDeal")}
                  </Button>
                }
                onDelete={() => setGalleryBulkDeleteOpen(true)}
                deleteDisabled={selectedIds.size === 0}
                deletePending={galleryDeleteBusy}
              />
            </div>
          </>
        ) : (
          <PageTitleBar
            className="h-full min-w-0 flex-1 border-0"
            rowClassName="h-full min-h-0 items-center gap-2"
            title={t("images.gallery")}
            right={
              <>
                {viewMode === "grid" ? (
                  photos.length > 0 ? (
                    isMobile ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 w-9 shrink-0 p-0"
                        aria-label={selectionMode ? t("images.cancelSelection") : t("images.selectPhotos")}
                        onClick={() => {
                          hapticTap(12);
                          if (selectionMode) exitSelectionMode();
                          else setSelectionMode(true);
                        }}
                      >
                        {selectionMode ? <X className="h-4 w-4" /> : <CheckSquare className="h-4 w-4" />}
                      </Button>
                    ) : null
                  ) : null
                ) : !isMobile ? (
                  <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-2">
                    <Button
                      size="sm"
                      type="button"
                      onClick={() => {
                        hapticTap(12);
                        setUploadSheetOpen(true);
                      }}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {t("images.uploadPhotosTitle")}
                    </Button>
                  </div>
                ) : null}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 w-9 shrink-0 p-0 md:hidden"
                  onClick={() => setFilterSheetOpen(true)}
                  aria-label={t("common.filterLabel")}
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </>
            }
          />
        )}
      </div>

      <div className="flex-1 min-w-0 overflow-hidden">
        <div className="grid h-full min-h-0 grid-cols-1 md:grid-cols-[360px,1fr] gap-3 md:gap-6">
      <div className="hidden md:block overflow-y-auto">
        <div className="p-4 space-y-4">
          <div className="p-4 border rounded-md bg-card">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                className="pl-10"
                placeholder={t("images.searchPlaceholder")}
              />
            </div>
          </div>
          <div className="p-4 border rounded-md bg-card">
            <ImageFilters
              filters={filters}
              onFiltersChange={setFilters}
              optionCounts={galleryOptionCounts}
              minDate={dateRangeQuery.data?.minDate}
              maxDate={dateRangeQuery.data?.maxDate}
              dateFilterEnabled={dateFilterEnabled}
              onDateFilterToggle={setDateFilterEnabled}
              hideFiltersTitle
            />
          </div>
        </div>
      </div>
      <div className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden">
        <div
          ref={scrollContainerRef}
          className={cn(
            "relative flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto",
            galleryFileDragHighlight && fileDropZoneActive && "ring-2 ring-primary ring-inset"
          )}
          onDragEnter={(e) => {
            if (!fileDropZoneActive || !dataTransferHasFilePayload(e.dataTransfer)) return;
            e.preventDefault();
            setGalleryFileDragHighlight(true);
          }}
          onDragLeave={(e) => {
            if (!fileDropZoneActive || !dataTransferHasFilePayload(e.dataTransfer)) return;
            const next = e.relatedTarget as Node | null;
            if (next && (e.currentTarget as Node).contains(next)) return;
            setGalleryFileDragHighlight(false);
          }}
          onDragOver={(e) => {
            if (!fileDropZoneActive || !dataTransferHasFilePayload(e.dataTransfer)) return;
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={(e) => {
            setGalleryFileDragHighlight(false);
            if (!fileDropZoneActive) return;
            e.preventDefault();
            e.stopPropagation();
            const list = filterImageFiles(Array.from(e.dataTransfer.files));
            if (list.length === 0) return;
            setGalleryDropPendingFiles(list);
            setGalleryDropDialogOpen(true);
          }}
        >
          {galleryFileDragHighlight && fileDropZoneActive && (
            <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
              <p className="rounded-lg border bg-card px-4 py-3 text-center text-sm font-medium shadow-lg">
                {t("images.dropReleaseHint")}
              </p>
            </div>
          )}
          <div className="container mx-auto w-full px-2 py-3 md:px-4 md:py-6">
            <ImagesSectionTabs
              active={viewMode === "map" ? "map" : "gallery"}
              compact={isMobile}
            />
            <div className="mt-4">
      {viewMode === "grid" ? (
        <>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : photos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
            <p className="text-lg">{t("images.noRawPhotos")}</p>
          </div>
        ) : (
          <div className="p-4 pb-8">
            {Object.keys(byMonth)
              .sort((a, b) => b.localeCompare(a))
              .map((monthKey) => {
                const monthPhotos = byMonth[monthKey];
                return (
                  <section key={monthKey} className="mb-10">
                    <h2 className="text-lg font-semibold text-foreground mb-0.5 sticky top-0 bg-background/95 py-2 z-10">
                      {monthLabels[monthKey]}
                    </h2>
                    {visibleRangeByMonth[monthKey] && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {visibleRangeByMonth[monthKey].first === visibleRangeByMonth[monthKey].last
                          ? visibleRangeByMonth[monthKey].first
                          : `${visibleRangeByMonth[monthKey].first} – ${visibleRangeByMonth[monthKey].last}`}
                      </p>
                    )}
                    <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2.5">
                      {monthPhotos.map((photo, index) => (
                        <GalleryPhotoTile
                          key={photo.id}
                          photo={photo}
                          monthKey={monthKey}
                          index={index}
                          isMobile={isMobile}
                          selectionMode={selectionMode}
                          isSelected={selectedIds.has(photo.id)}
                          onEnterSelectionMode={() => {
                            hapticTap(12);
                            setSelectionMode(true);
                            setSelectedIds(new Set([photo.id]));
                          }}
                          onToggle={() => togglePhotoSelection(photo.id)}
                          onOpenViewer={() => setViewerState({ photos: flatPhotos, index: getPhotoIndex(photo.id) })}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

            {nextBefore && (
              <div ref={sentinelRef} className="flex justify-center py-6">
                {loadingMore && (
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                )}
              </div>
            )}
          </div>
        )}
        </>
      ) : (
      <div className="w-full shrink-0 flex flex-col p-2">
        {geoQuery.isLoading ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <span>{t("images.mapLoading")}</span>
          </div>
        ) : geoQuery.isError ? (
          <div className="flex items-center justify-center p-8 text-destructive text-center">
            {t("images.mapGeoFetchError")}
          </div>
        ) : !geoQuery.data?.length ? (
          <div className="flex flex-col items-center justify-center py-16 px-4 text-muted-foreground text-center max-w-md mx-auto">
            <p>{t("images.mapNoGeotagged")}</p>
          </div>
        ) : (
          <GalleryMapView
            key={`gallery-map-${location.key}`}
            className="w-full"
            photos={geoQuery.data}
            apiKey={mapsApiKey}
            onPhotoClick={handleMapPhotoOpen}
            onClusterPhotosClick={handleMapClusterOpen}
            labels={{
              loadError: t("images.mapLoadError"),
              apiMissing: t("images.mapApiMissing"),
            }}
          />
        )}
      </div>
      )}
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>

      {viewerState && (
        <PhotoViewer
          photos={viewerState.photos}
          currentIndex={viewerState.index}
          onClose={() => setViewerState(null)}
          onNavigate={(index) => setViewerState((s) => (s ? { ...s, index } : null))}
          getPhotoLabel={(p) => formatUploadedAt(p.created_at, locale, p.gallery_sort_at)}
          onOpenInFolder={(folderId) => {
            setViewerState(null);
            navigate(`/images/${folderId}`);
          }}
          openInFolderLabel={t("images.openInFolder")}
        />
      )}

      {isMobile && (
        <>
          {/* fab-token: unified D */}
          <FabMenu
            alwaysShowMenu
            fabIcon={<Camera className="h-6 w-6" />}
            fabAriaLabel={t("images.uploadPhotosTitle")}
            hidden={selectionMode}
            actions={[
              {
                id: "upload",
                label: t("images.uploadPhotosTitle"),
                icon: <Upload className="h-5 w-5" />,
                onClick: () => setUploadSheetOpen(true),
              },
            ]}
          />
        </>
      )}

      {isMobile && viewMode === "grid" && selectionMode && (
        <div className="fixed inset-x-0 z-40 px-3 pb-2" style={{ bottom: "calc(4rem + env(safe-area-inset-bottom))" }}>
          {/* header-overflow: unified E */}
          <div className="mx-auto flex max-w-xl flex-wrap items-center gap-2 rounded-xl border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
            {gallerySelectionActions}
          </div>
        </div>
      )}

      <AppSheet
        open={uploadSheetOpen}
        onOpenChange={setUploadSheetOpen}
        title={t("images.uploadPhotosTitle")}
      >
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">
            {t("images.galleryUploadHint")}
          </p>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="add-to-deal"
              checked={addToDeal}
              onCheckedChange={(checked) => {
                setAddToDeal(!!checked);
                if (!checked) setSelectedDealId(null);
              }}
            />
            <label htmlFor="add-to-deal" className="text-sm font-medium cursor-pointer">
              {t("images.addToDeal")}
            </label>
          </div>
          {addToDeal && (
            <>
              <div className="space-y-2">
                <Label>{t("images.searchDealPlaceholder")}</Label>
                <Input
                  placeholder={t("images.searchDealPlaceholder")}
                  value={dealSearchValue}
                  onChange={(e) => setDealSearchValue(e.target.value)}
                  className="w-full"
                />
                <div className="max-h-[200px] overflow-y-auto border rounded-md p-1 space-y-0.5">
                  {boardQuery.isLoading ? (
                    <div className="flex justify-center py-2">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    filteredDeals.slice(0, 50).map((deal) => (
                      <button
                        key={deal.id}
                        type="button"
                        className={cn(
                          "w-full text-left px-2 py-1.5 rounded text-sm",
                          selectedDealId === deal.id
                            ? "bg-primary text-primary-foreground"
                            : "hover:bg-muted"
                        )}
                        onClick={() => setSelectedDealId(deal.id)}
                      >
                        {(deal as { title?: string }).title || deal.id}
                      </button>
                    ))
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t("images.uploadSubfolderType")}</Label>
                <Select
                  value={uploadSubfolderType}
                  onValueChange={(v: "bemeres" | "munkaink" | "marketing") => setUploadSubfolderType(v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                    <SelectItem value="munkaink">{t("images.munkaink")}</SelectItem>
                    <SelectItem value="marketing">{t("images.marketing")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
          <MultiPhotoCapture
            onUpload={handleGalleryUpload}
            uploadBusy={uploadBusy}
            disabled={addToDeal && !selectedDealId}
            cameraLabel={t("images.takePhotos")}
            chooseFilesLabel={t("images.chooseFromFiles")}
            addPhotoLabel={t("images.addPhoto")}
            uploadLabel={t("images.uploadAll")}
            uploadingLabel={t("images.uploading")}
            seedFiles={galleryUploadSeedFiles}
            onSeedFilesConsumed={clearGalleryUploadSeed}
          />
        </div>
      </AppSheet>

      <AppSheet
        open={addToDealSheetOpen}
        onOpenChange={setAddToDealSheetOpen}
        title={t("images.addToDeal")}
      >
        <div className="space-y-4 py-4">
          <p className="text-sm text-muted-foreground">{t("images.moveToDealHint")}</p>
          <div className="space-y-2">
            <Label>{t("images.uploadSubfolderType")}</Label>
            <Select
              value={moveSubfolderType}
              onValueChange={(v: "bemeres" | "munkaink" | "marketing") => setMoveSubfolderType(v)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bemeres">{t("images.bemeres")}</SelectItem>
                <SelectItem value="munkaink">{t("images.munkaink")}</SelectItem>
                <SelectItem value="marketing">{t("images.marketing")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Label>{t("images.searchDealPlaceholder")}</Label>
          <Input
            placeholder={t("images.searchDealPlaceholder")}
            value={dealSearchValue}
            onChange={(e) => setDealSearchValue(e.target.value)}
            className="w-full"
          />
          <div className="max-h-[60vh] overflow-y-auto space-y-1">
            {boardQuery.isLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              filteredDeals.slice(0, 100).map((deal) => (
                <Button
                  key={deal.id}
                  variant="ghost"
                  className="w-full justify-start text-left h-auto py-2"
                  onClick={() => handleAddToDeal(deal.id)}
                  disabled={moveBusy}
                >
                  {(deal as { title?: string }).title || deal.id}
                </Button>
              ))
            )}
          </div>
          {filteredDeals.length === 0 && !boardQuery.isLoading && (
            <p className="text-sm text-muted-foreground">{t("images.noDealsFound")}</p>
          )}
        </div>
      </AppSheet>

      <AppSheet
        open={socialSheetOpen}
        onOpenChange={setSocialSheetOpen}
        title={t("social.title")}
      >
        <div className="space-y-6 py-4">
          <p className="text-sm text-muted-foreground">{t("social.hint")}</p>
          <SocialComposer
            photoIds={Array.from(selectedIds)}
            onGenerated={(assets) => {
              setGeneratedSocialAssets(assets);
              void queryClient.invalidateQueries({ queryKey: queryKeys.socialAssetsByPhotoIds(Array.from(selectedIds)) });
            }}
          />
          <SocialAssetsPreview assets={generatedSocialAssets} />
        </div>
      </AppSheet>

      <AppSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        title={t("common.filterLabel")}
      >
        <div className="space-y-4 py-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              className="pl-10"
              placeholder={t("images.searchPlaceholder")}
            />
          </div>
          <ImageFilters
            filters={filters}
            onFiltersChange={setFilters}
            optionCounts={galleryOptionCounts}
            minDate={dateRangeQuery.data?.minDate}
            maxDate={dateRangeQuery.data?.maxDate}
            dateFilterEnabled={dateFilterEnabled}
            onDateFilterToggle={setDateFilterEnabled}
            hideFiltersTitle
          />
        </div>
      </AppSheet>

      <Dialog
        open={galleryDropDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeGalleryDropDialog();
          else setGalleryDropDialogOpen(true);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("images.galleryDropTitle")}</DialogTitle>
            <DialogDescription>
              {t("images.galleryDropDescription", { count: galleryDropPendingFiles.length })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="button"
              className="w-full"
              disabled={galleryDropQuickBusy}
              onClick={() => void confirmGalleryDropQuickUpload()}
            >
              {galleryDropQuickBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("images.uploading")}
                </>
              ) : (
                t("images.galleryDropQuick")
              )}
            </Button>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              disabled={galleryDropQuickBusy}
              onClick={openGalleryDropUploadSheet}
            >
              {t("images.galleryDropOpenSheet")}
            </Button>
            <Button type="button" variant="ghost" className="w-full" onClick={closeGalleryDropDialog} disabled={galleryDropQuickBusy}>
              {t("common.cancel")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={galleryBulkDeleteOpen} onOpenChange={setGalleryBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("images.galleryBulkDeleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("images.galleryBulkDeleteDescription", { count: selectedIds.size })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={galleryDeleteBusy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={galleryDeleteBusy}
              onClick={(e) => {
                e.preventDefault();
                void confirmGalleryBulkDelete();
              }}
            >
              {galleryDeleteBusy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                  {t("common.loading")}
                </>
              ) : (
                t("images.deleteSelectedPhotos")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
